// VideoPlayer.jsx — safe-guarded version with robust YouTube ID extractor (drop-in)
import React from 'react'

/**
 * Extracts a YouTube video ID from a variety of inputs.
 * Returns the 11-character ID string, or null if not found.
 */
function getYouTubeId(input) {
  if (!input) return null
  const s = String(input).trim()

  // If it's already an 11-char video id (common case), return it
  const possibleId = s.match(/^[A-Za-z0-9_-]{11}$/)
  if (possibleId) return possibleId[0]

  // 1) Standard watch?v=VIDEOID (may have other params)
  const watchMatch = s.match(/[?&]v=([A-Za-z0-9_-]{11})/)
  if (watchMatch) return watchMatch[1]

  // 2) youtu.be/VIDEOID
  const shortMatch = s.match(/youtu\.be\/([A-Za-z0-9_-]{11})/)
  if (shortMatch) return shortMatch[1]

  // 3) /embed/VIDEOID or /v/VIDEOID
  const embedMatch = s.match(/\/(?:embed|v)\/([A-Za-z0-9_-]{11})/)
  if (embedMatch) return embedMatch[1]

  // 4) sometimes id appears as "vi/VIDEOID" in odd embed forms
  const viMatch = s.match(/vi\/([A-Za-z0-9_-]{11})/)
  if (viMatch) return viMatch[1]

  // 5) fallback: try to find the first 11-char token in the string
  const anyId = s.match(/([A-Za-z0-9_-]{11})/)
  if (anyId) return anyId[1]

  return null
}

const VideoPlayer = ({
  assetUrl,
  setShowVideoPrev,
  mouseOverPreview,
  setMouseOverPreview,
  idx,
}) => {
  const iframeRef = React.useRef(null)

  // robustly extract the video id
  const videoId = getYouTubeId(assetUrl)
  const ytVideoURL = videoId
    ? `https://www.youtube.com/embed/${videoId}?rel=0&amp;autoplay=1&mute=1&controls=0&autohide=1&showinfo=0&modestbranding=1&enablejsapi=1`
    : ''

  const [ytvUrl, setvideoUrl] = React.useState('')

  // keep the exact previewStartTime logic ( 7000 multiplier here)
  const previewStartTime = (order) => (order === 0 ? 1500 : order * 7000)

  // refs to keep timers/listeners so we can clean them reliably
  const scheduledTimersRef = React.useRef([])
  const loadListenerRef = React.useRef(null)

  React.useEffect(() => {
    // Clear any previous timers/listener
    scheduledTimersRef.current.forEach((t) => clearTimeout(t))
    scheduledTimersRef.current = []
    if (loadListenerRef.current && iframeRef.current) {
      iframeRef.current.removeEventListener('load', loadListenerRef.current, {
        passive: true,
      })
      loadListenerRef.current = null
    }

    const startMs = previewStartTime(idx)

    // 1) schedule iframe src set at startMs
    const setSrcTimer = setTimeout(() => {
      // only set src if we have a valid URL (videoId); otherwise do nothing (fail-safe)
      if (ytVideoURL) {
        setvideoUrl(ytVideoURL)
      }

      // attach load listener once we set the src (or attach earlier — safe to attach now)
      if (iframeRef.current) {
        // create listener
        const onLoad = () => {
          // When iframe actually loads, start fade timers relative to *now*
          // Fade OUT (show video preview) after 1300ms
          const fadeOutTimer = setTimeout(() => {
            setShowVideoPrev(true)
          }, 1300)

          // Fade IN (hide video preview) after 6000ms
          const fadeInTimer = setTimeout(() => {
            setShowVideoPrev(false)
          }, 6000)

          // store timers for cleanup
          scheduledTimersRef.current.push(fadeOutTimer, fadeInTimer)
        }

        // store ref so we can remove it on cleanup
        loadListenerRef.current = onLoad
        iframeRef.current.addEventListener('load', onLoad, { passive: true })
      }
    }, startMs)

    // keep the timer reference
    scheduledTimersRef.current.push(setSrcTimer)

    // cleanup on unmount or idx change
    return () => {
      scheduledTimersRef.current.forEach((t) => clearTimeout(t))
      scheduledTimersRef.current = []
      if (loadListenerRef.current && iframeRef.current) {
        iframeRef.current.removeEventListener('load', loadListenerRef.current, {
          passive: true,
        })
        loadListenerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, ytVideoURL, setShowVideoPrev])

  // mouseOver preview behaviour (unchanged, but always returns cleanup)
  React.useEffect(() => {
    let mousePreviewTimer

    if (mouseOverPreview) {
      setShowVideoPrev(true)

      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          '*',
        )
      }

      mousePreviewTimer = setTimeout(() => {
        setShowVideoPrev(false)
        setMouseOverPreview(false)

        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*',
          )
        }
      }, 3000)
    }

    // ALWAYS return cleanup (even if timer undefined)
    return () => {
      if (mousePreviewTimer) clearTimeout(mousePreviewTimer)
    }
  }, [mouseOverPreview, setMouseOverPreview, setShowVideoPrev])

  return (
    <div
      onMouseEnter={() => {
        // Play video on mouse enter
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            '{"event":"command","func":"playVideo","args":""}',
            '*',
          )
        }
      }}
      onMouseLeave={() => {
        // Pause video on mouse leave
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*',
          )
        }
      }}
    >
      <iframe
        allow=""
        style={{
          pointerEvents: 'none',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
        }}
        ref={iframeRef}
        height={180}
        width={350}
        src={ytvUrl}
        allowFullScreen
        title="Embedded youtube"
      />
    </div>
  )
}

export default VideoPlayer
// // VideoPlayer.jsx — safe-guarded version (drop-in)
// import React from 'react'

// const VideoPlayer = ({
//   assetUrl,
//   setShowVideoPrev,
//   mouseOverPreview,
//   setMouseOverPreview,
//   idx,
// }) => {
//   const iframeRef = React.useRef(null)
//   const ytVideoId = assetUrl.split('=')
//   const ytVideoURL = `https://www.youtube.com/embed/${ytVideoId[1]}?rel=0&amp;autoplay=1&mute=1&controls=0&autohide=1&showinfo=0&modestbranding=1&enablejsapi=1`
//   const [ytvUrl, setvideoUrl] = React.useState('')

//   // keep the exact previewStartTime logic
//   const previewStartTime = (order) => (order === 0 ? 1500 : order * 7000)

//   // refs to keep timers/listeners so we can clean them reliably
//   const scheduledTimersRef = React.useRef([])
//   const loadListenerRef = React.useRef(null)

//   React.useEffect(() => {
//     // Clear any previous timers/listener
//     scheduledTimersRef.current.forEach((t) => clearTimeout(t))
//     scheduledTimersRef.current = []
//     if (loadListenerRef.current && iframeRef.current) {
//       iframeRef.current.removeEventListener('load', loadListenerRef.current, {
//         passive: true,
//       })
//       loadListenerRef.current = null
//     }

//     const startMs = previewStartTime(idx)

//     // 1) schedule iframe src set at startMs
//     const setSrcTimer = setTimeout(() => {
//       setvideoUrl(ytVideoURL)

//       // attach load listener once we set the src (or attach earlier — safe to attach now)
//       if (iframeRef.current) {
//         // create listener
//         const onLoad = () => {
//           // When iframe actually loads, start fade timers relative to *now*
//           // Fade OUT (show video preview) after 1300ms
//           const fadeOutTimer = setTimeout(() => {
//             setShowVideoPrev(true)
//           }, 1300)

//           // Fade IN (hide video preview) after 6000ms
//           const fadeInTimer = setTimeout(() => {
//             setShowVideoPrev(false)
//           }, 6000)

//           // store timers for cleanup
//           scheduledTimersRef.current.push(fadeOutTimer, fadeInTimer)
//         }

//         // store ref so we can remove it on cleanup
//         loadListenerRef.current = onLoad
//         iframeRef.current.addEventListener('load', onLoad, { passive: true })
//       }
//     }, startMs)

//     // keep the timer reference
//     scheduledTimersRef.current.push(setSrcTimer)

//     // cleanup on unmount or idx change
//     return () => {
//       scheduledTimersRef.current.forEach((t) => clearTimeout(t))
//       scheduledTimersRef.current = []
//       if (loadListenerRef.current && iframeRef.current) {
//         iframeRef.current.removeEventListener('load', loadListenerRef.current, {
//           passive: true,
//         })
//         loadListenerRef.current = null
//       }
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [idx, ytVideoURL, setShowVideoPrev])

//   // mouseOver preview behaviour (unchanged)
//   React.useEffect(() => {
//     let mousePreviewTimer

//     if (mouseOverPreview) {
//       setShowVideoPrev(true)

//       if (iframeRef.current && iframeRef.current.contentWindow) {
//         iframeRef.current.contentWindow.postMessage(
//           '{"event":"command","func":"playVideo","args":""}',
//           '*',
//         )
//       }

//       mousePreviewTimer = setTimeout(() => {
//         setShowVideoPrev(false)
//         setMouseOverPreview(false)

//         if (iframeRef.current && iframeRef.current.contentWindow) {
//           iframeRef.current.contentWindow.postMessage(
//             '{"event":"command","func":"pauseVideo","args":""}',
//             '*',
//           )
//         }
//       }, 3000)
//     }

//     // ALWAYS return cleanup (even if timer undefined)
//     return () => {
//       if (mousePreviewTimer) clearTimeout(mousePreviewTimer)
//     }
//   }, [mouseOverPreview, setMouseOverPreview, setShowVideoPrev])

//   return (
//     <div
//       onMouseEnter={() => {
//         // Play video on mouse enter
//         if (iframeRef.current && iframeRef.current.contentWindow) {
//           iframeRef.current.contentWindow.postMessage(
//             '{"event":"command","func":"playVideo","args":""}',
//             '*',
//           )
//         }
//       }}
//       onMouseLeave={() => {
//         // Pause video on mouse leave
//         if (iframeRef.current && iframeRef.current.contentWindow) {
//           iframeRef.current.contentWindow.postMessage(
//             '{"event":"command","func":"pauseVideo","args":""}',
//             '*',
//           )
//         }
//       }}
//     >
//       <iframe
//         allow=""
//         style={{ pointerEvents: 'none' }}
//         ref={iframeRef}
//         height={180}
//         width={350}
//         src={ytvUrl}
//         allowFullScreen
//         title="Embedded youtube"
//       />
//     </div>
//   )
// }

// export default VideoPlayer
